import './bootstrap';

import Alpine from 'alpinejs';
import { createApp } from 'vue';

// PrimeVue setup, shared by the my-page app (for the DatePicker) and the admin apps
import PrimeVue from 'primevue/config';
import ToastService from 'primevue/toastservice';
import ConfirmationService from 'primevue/confirmationservice';
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import nbLocale from './vue/primevue-locale-nb';

// Vue app for my page toplist component
import Mypage from './vue/Mypage';

// Vue app for statistics component
import Toplist from './vue/Toplist';

// Vue apps for admin pages using PrimeVue instead of Bootstrap
import UserGroupsTable from './vue/components/UserGroupsTable';
import UsersTable from './vue/components/UsersTable';
import LotteryDraw from './vue/components/LotteryDraw';
import CacheSettings from './vue/components/CacheSettings';

// Vue apps for the per-item-group user toplist tables (one tab/mount per item group)
import UserToplistTable from './vue/components/UserToplistTable';

// Vue apps for the per-item-group user group toplist tables (one tab/mount per item group)
import UserGroupToplistTable from './vue/components/UserGroupToplistTable';

// Vue app for the admin statistics activity chart
import ActivityLineGraph from './vue/components/ActivityLineGraph';

// Vue app for the admin statistics active-users-per-year chart
import ActiveUsersBarChart from './vue/components/ActiveUsersBarChart';

// Vue app for the admin dashboard recent-activity chart
import DashboardActivityChart from './vue/components/DashboardActivityChart';

// Vue apps for the per-item-group quantity charts (one tab/mount per item group)
import ItemQuantitiesChart from './vue/components/ItemQuantitiesChart';

import { initQuillFields } from './admin/quill-init';

window.Alpine = Alpine;
Alpine.start();

const BluePreset = definePreset(Aura, {
    semantic: {
        primary: {
            50: '{blue.50}',
            100: '{blue.100}',
            200: '{blue.200}',
            300: '{blue.300}',
            400: '{blue.400}',
            500: '{blue.500}',
            600: '{blue.600}',
            700: '{blue.700}',
            800: '{blue.800}',
            900: '{blue.900}',
            950: '{blue.950}'
        }
    }
});

const primeVueOptions = {
    theme: {
        preset: BluePreset,
        options: {
            darkModeSelector: false
        }
    },
    locale: nbLocale
};

const myPageElement = document.querySelector('[data-mypage-app]');
if (myPageElement) {
    const app = createApp({
        components: {
            Mypage
        }
    });

    app.use(PrimeVue, primeVueOptions);

    app.mount(myPageElement);
}

const statisticsElement = document.querySelector('[data-statistics-app]');
if (statisticsElement) {
    const app = createApp({
        components: {
            Toplist
        }
    });

    app.use(PrimeVue, primeVueOptions);

    app.mount(statisticsElement);
}

function mountPrimeVueApp(selector, components) {
    const element = document.querySelector(selector);
    if (!element) {
        return;
    }

    const app = createApp({ components });

    app.use(PrimeVue, primeVueOptions);
    app.use(ToastService);
    app.use(ConfirmationService);
    app.mount(element);
}

function mountPrimeVueApps(selector, components) {
    document.querySelectorAll(selector).forEach((element) => {
        const app = createApp({ components });

        app.use(PrimeVue, primeVueOptions);
        app.use(ToastService);
        app.use(ConfirmationService);
        app.mount(element);
    });
}

mountPrimeVueApp('[data-usergroups-app]', { UserGroupsTable });
mountPrimeVueApp('[data-users-app]', { UsersTable });
mountPrimeVueApp('[data-lottery-app]', { LotteryDraw });
mountPrimeVueApp('[data-settings-app]', { CacheSettings });

mountPrimeVueApps('[data-toplist-users-app]', { UserToplistTable });
mountPrimeVueApps('[data-toplist-usergroups-app]', { UserGroupToplistTable });

const adminStatisticsElement = document.querySelector('[data-admin-statistics-app]');
if (adminStatisticsElement) {
    createApp({
        components: {
            ActivityLineGraph,
            ActiveUsersBarChart
        }
    }).mount(adminStatisticsElement);
}

const dashboardActivityElement = document.querySelector('[data-dashboard-activity-app]');
if (dashboardActivityElement) {
    createApp({
        components: {
            DashboardActivityChart
        }
    }).mount(dashboardActivityElement);
}

document.querySelectorAll('[data-itemgroup-quantities-app]').forEach((element) => {
    createApp({
        components: {
            ItemQuantitiesChart
        }
    }).mount(element);
});

initQuillFields();
